import { Button } from "@aurumiq/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PageHeader } from "@/app/page-header";
import { useAuth } from "@/auth/auth-context";
import { useMe } from "@/auth/use-permission";
import { TextField } from "@/components/field";
import { Stat, StatList } from "@/components/query-state";
import { useChangeOwnPassword } from "@/features/staff/queries";
import { passwordSchema } from "@/features/staff/staff-dialogs";

const schema = z
  .object({
    current_password: z.string().min(1, "Required"),
    new_password: passwordSchema,
    confirm: z.string(),
  })
  .refine((v) => v.new_password === v.confirm, {
    path: ["confirm"],
    message: "Passwords do not match",
  });
type FormValues = z.infer<typeof schema>;

export function AccountPage() {
  const auth = useAuth();
  const me = useMe();
  const change = useChangeOwnPassword();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { current_password: "", new_password: "", confirm: "" },
  });
  const err = (k: keyof FormValues) => form.formState.errors[k]?.message;
  const onSubmit = form.handleSubmit(async (v) => {
    await change.mutateAsync({
      current_password: v.current_password,
      new_password: v.new_password,
    });
    form.reset();
  });
  return (
    <>
      <PageHeader title="Account" description="Your staff profile and local password." />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <StatList>
              <Stat label="Name">{me.data?.name ?? auth.user?.name ?? "—"}</Stat>
              <Stat label="Email">{me.data?.email ?? auth.user?.email ?? "—"}</Stat>
              <Stat label="Role">
                {me.data?.role ? (
                  <StatusTag variant="gold">{me.data.role.toUpperCase()}</StatusTag>
                ) : (
                  "—"
                )}
              </Stat>
              <Stat label="Sign-in">{auth.mode === "test" ? "test mode" : "staff session"}</Stat>
            </StatList>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Change password</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="grid gap-3">
              <TextField
                label="Current password"
                type="password"
                autoComplete="current-password"
                error={err("current_password")}
                {...form.register("current_password")}
              />
              <TextField
                label="New password"
                type="password"
                autoComplete="new-password"
                hint="At least 12 characters."
                error={err("new_password")}
                {...form.register("new_password")}
              />
              <TextField
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                error={err("confirm")}
                {...form.register("confirm")}
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={change.isPending}>
                  {change.isPending ? "Saving…" : "Change password"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
